import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import useHoverSupport from "../../../hooks/useHoverSupport";
import { Spinner } from "../Spinner";

const DEFAULT_OFFSET = 16;
const NAVIGATION_HEIGHT = 64;
const PADDING = 16;

const ensurePositiveNumber = value =>
  Number.isFinite(value) && value > 0 ? value : 1;

const resolveOffset = value =>
  Number.isFinite(value) ? Math.max(value, 0) : DEFAULT_OFFSET;

const getScaledDimensions = (
  naturalWidth,
  naturalHeight,
  maxWidth,
  maxHeight,
) => {
  const safeMaxWidth = ensurePositiveNumber(maxWidth);
  const safeMaxHeight = ensurePositiveNumber(maxHeight);

  if (!naturalWidth || !naturalHeight) {
    return { width: safeMaxWidth, height: safeMaxHeight };
  }

  const widthScale = safeMaxWidth / naturalWidth;
  const heightScale = safeMaxHeight / naturalHeight;
  const scale = Math.min(widthScale, heightScale, 1);

  return {
    width: Math.max(Math.round(naturalWidth * scale), 1),
    height: Math.max(Math.round(naturalHeight * scale), 1),
  };
};

const clampWithinViewport = (position, size, offset) => {
  const { innerHeight, innerWidth } = window;
  const safeOffset = ensurePositiveNumber(offset);
  const maxLeft = innerWidth - safeOffset - size.width;
  const maxTop = innerHeight - safeOffset - size.height;

  return {
    top: Math.min(Math.max(position.top, safeOffset), Math.max(safeOffset, maxTop)),
    left: Math.min(Math.max(position.left, safeOffset), Math.max(safeOffset, maxLeft)),
  };
};

const ImageZoom = props => {
  const {
    alt = "Preview image",
    className = "",
    offset = DEFAULT_OFFSET,
    onClick,
    src,
    zoomSrc,
    onImageError,
    onImageLoad,
  } = props || {};

  const safeOffset = useMemo(() => resolveOffset(offset), [offset]);

  const [imageDimensions, setImageDimensions] = useState({ width: null, height: null });

  const calculateZoomDimensions = useCallback((naturalWidth, naturalHeight) => {
    const availableHeight = window.innerHeight - NAVIGATION_HEIGHT; // 16px padding top and bottom
    const availableWidth = window.innerWidth - (2 * PADDING); // 16px padding left and right

    // Use 80% of viewport or available space, whichever is smaller, with min of 350px
    const calculatedWidth = Math.max(200, Math.min(availableWidth * 0.50, 800));
    const calculatedHeight = Math.max(200, Math.min(availableHeight * 0.50, 800));

    // If we have image dimensions, respect aspect ratio
    if (naturalWidth && naturalHeight) {
      const aspectRatio = naturalWidth / naturalHeight;
      
      if (aspectRatio > 1) {
        // Width is larger than height (landscape): limit by max width
        const width = calculatedWidth;
        const height = width / aspectRatio;
        return {
          width: Math.round(width),
          height: Math.round(height),
        };
      } else {
        // Height is larger than width (portrait): limit by max height
        const height = calculatedHeight;
        const width = height * aspectRatio;
        return {
          width: Math.round(width),
          height: Math.round(height),
        };
      }
    }

    return {
      width: calculatedWidth,
      height: calculatedHeight,
    };
  }, []);

  const [resolvedZoomDimensions, setResolvedZoomDimensions] = useState(() =>
    calculateZoomDimensions(null, null)
  );

  const { width: maxZoomWidth, height: maxZoomHeight } = resolvedZoomDimensions;

  const [isVisible, setIsVisible] = useState(false);
  const [zoomSize, setZoomSize] = useState(resolvedZoomDimensions);
  const [position, setPosition] = useState(() => ({
    top: safeOffset,
    left: safeOffset,
    width: maxZoomWidth,
    height: maxZoomHeight,
  }));
  const [hasError, setHasError] = useState(false);
  const [isZoomImageLoading, setIsZoomImageLoading] = useState(false);
  const [mountNode, setMountNode] = useState(null);
  const isHoverSupported = useHoverSupport();

  const resolvedZoomSrc = zoomSrc || src;

  const updatePosition = useCallback(
    event => {
      if (!resolvedZoomSrc) return;

      const { clientX, clientY } = event;
      const width = zoomSize.width;
      const height = zoomSize.height;
      const halfHeight = height / 2;

      let top = clientY - halfHeight;
      let left = clientX + safeOffset;

      const { innerHeight, innerWidth } = window;

      if (top < safeOffset) {
        top = safeOffset;
      }

      if (top + height > innerHeight - safeOffset) {
        top = Math.max(safeOffset, innerHeight - height - safeOffset);
      }

      if (left + width > innerWidth - safeOffset) {
        left = clientX - width - safeOffset;
      }

      const clamped = clampWithinViewport({ top, left }, { width, height }, safeOffset);

      setPosition({ top: clamped.top, left: clamped.left, width, height });
    },
    [resolvedZoomSrc, safeOffset, zoomSize.height, zoomSize.width],
  );

  useEffect(() => {
    setMountNode(document.body);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setResolvedZoomDimensions(calculateZoomDimensions(imageDimensions.width, imageDimensions.height));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateZoomDimensions, imageDimensions]);

  // Update resolvedZoomDimensions when imageDimensions change
  useEffect(() => {
    if (imageDimensions.width && imageDimensions.height) {
      setResolvedZoomDimensions(calculateZoomDimensions(imageDimensions.width, imageDimensions.height));
    }
  }, [imageDimensions, calculateZoomDimensions]);

  useEffect(() => {
    // Only update zoomSize if we have image dimensions to calculate proper aspect ratio
    // Otherwise, let handleZoomImageLoad set it when the image loads
    if (imageDimensions.width && imageDimensions.height) {
      const size = getScaledDimensions(
        imageDimensions.width,
        imageDimensions.height,
        maxZoomWidth,
        maxZoomHeight,
      );

      setZoomSize(prev => {
        if (prev.width === size.width && prev.height === size.height) {
          return prev;
        }
        return size;
      });

      setPosition(prev => {
        const clamped = clampWithinViewport(
          { top: prev.top ?? safeOffset, left: prev.left ?? safeOffset },
          size,
          safeOffset,
        );

        return {
          top: clamped.top,
          left: clamped.left,
          width: size.width,
          height: size.height,
        };
      });
    } else {
      // If no image dimensions yet, just update position with max bounds for initial positioning
      setPosition(prev => {
        const nextSize = { width: maxZoomWidth, height: maxZoomHeight };
        const clamped = clampWithinViewport(
          { top: prev.top ?? safeOffset, left: prev.left ?? safeOffset },
          nextSize,
          safeOffset,
        );

        return {
          top: clamped.top,
          left: clamped.left,
          width: nextSize.width,
          height: nextSize.height,
        };
      });
    }

    setHasError(false);
    if (isVisible && resolvedZoomSrc) {
      setIsZoomImageLoading(true);
    }
  }, [maxZoomHeight, maxZoomWidth, resolvedZoomSrc, safeOffset, isVisible, imageDimensions]);

  useEffect(() => {
    if (!resolvedZoomSrc || !isHoverSupported) {
      setIsVisible(false);
    }
  }, [resolvedZoomSrc, isHoverSupported]);

  const [isHovering, setIsHovering] = useState(false);
  const handleMouseEnter = useCallback(
    event => {
      // Prevent duplicate hover events
      if (isHovering) {
        return;
      }
      setIsHovering(true);
      if (!resolvedZoomSrc || !isHoverSupported) return;

      const allZoomElements = document.querySelectorAll('[data-image-zoom-element]');
      allZoomElements.forEach(element => {
        element.style.display = 'none';
      });

      updatePosition(event);
      setIsVisible(true);
      setIsZoomImageLoading(true);
      setHasError(false);
    },
    [resolvedZoomSrc, updatePosition, isHoverSupported, isHovering],
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setIsVisible(false);
    setIsZoomImageLoading(false);
  }, []);

  const handleMouseMove = useCallback(
    event => {
      if (!resolvedZoomSrc || !isVisible || !isHoverSupported) return;
      updatePosition(event);
    },
    [isVisible, resolvedZoomSrc, updatePosition, isHoverSupported],
  );

  const handleClick = useCallback(
    event => {
      setIsHovering(false);
      if (typeof onClick === "function") {
        onClick(event);
        return;
      }
    },
    [onClick],
  );

  const handleImageError = useCallback(
    event => {
      setHasError(true);
      onImageError?.(event);
    },
    [onImageError],
  );

  const handleImageLoad = useCallback(
    event => {
      const { naturalWidth, naturalHeight } = event?.currentTarget || {};
      if (naturalWidth && naturalHeight) {
        setImageDimensions({ width: naturalWidth, height: naturalHeight });
      }
      setHasError(false);
      onImageLoad?.(event);
    },
    [onImageLoad],
  );

  const handleZoomImageLoad = useCallback(
    event => {
      const { naturalWidth, naturalHeight } = event?.currentTarget || {};
      
      // Update image dimensions if not already set or if zoom image has different dimensions
      if (naturalWidth && naturalHeight) {
        setImageDimensions(prev => {
          // Only update if dimensions changed
          if (prev.width !== naturalWidth || prev.height !== naturalHeight) {
            return { width: naturalWidth, height: naturalHeight };
          }
          return prev;
        });
      }
      
      const size = getScaledDimensions(
        naturalWidth,
        naturalHeight,
        maxZoomWidth,
        maxZoomHeight,
      );

      setZoomSize(prev => {
        if (prev.width === size.width && prev.height === size.height) {
          return prev;
        }
        return size;
      });

      setPosition(prev => {
        const clamped = clampWithinViewport(
          { top: prev.top, left: prev.left },
          size,
          safeOffset,
        );

        return {
          top: clamped.top,
          left: clamped.left,
          width: size.width,
          height: size.height,
        };
      });

      setHasError(false);
      setIsZoomImageLoading(false);
    },
    [maxZoomHeight, maxZoomWidth, safeOffset],
  );

  const handleZoomImageError = useCallback(
    event => {
      setHasError(true);
      setIsZoomImageLoading(false);
      onImageError?.(event);
    },
    [onImageError],
  );

  useEffect(() => {
    if (!resolvedZoomSrc) {
      setHasError(false);
      setIsZoomImageLoading(false);
    }
  }, [resolvedZoomSrc]);

  // Preload zoom image (hidden) when hover starts
  const zoomImagePreloader = useMemo(() => {
    if (!isHoverSupported || !isVisible || !resolvedZoomSrc || hasError || !isZoomImageLoading) {
      return null;
    }

    return (
      <img
        alt=""
        className="hidden"
        src={resolvedZoomSrc}
        onError={handleZoomImageError}
        onLoad={handleZoomImageLoad}
      />
    );
  }, [
    hasError,
    isVisible,
    isZoomImageLoading,
    resolvedZoomSrc,
    handleZoomImageError,
    handleZoomImageLoad,
    isHoverSupported,
  ]);

  const zoomElement = useMemo(() => {
    if (!isHoverSupported || !isVisible || !mountNode || !resolvedZoomSrc || hasError || isZoomImageLoading) {
      return null;
    }

    const style = {
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height,
    };

    return createPortal(
      <div
        data-image-zoom-element
        className="pointer-events-none fixed z-[60] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-2xl"
        style={{
          ...style,
          animation: "fadeIn 0.2s ease-in-out forwards",
        }}
      >
        <img
          alt={alt}
          className="h-full w-full object-contain"
          src={resolvedZoomSrc}
          onError={handleZoomImageError}
          onLoad={handleZoomImageLoad}
        />
      </div>,
      mountNode,
    );
  }, [
    alt,
    hasError,
    isVisible,
    isZoomImageLoading,
    mountNode,
    position,
    resolvedZoomSrc,
    handleZoomImageError,
    handleZoomImageLoad,
  ]);

  if (!src) {
    return null;
  }

  const cursorClass = onClick ? "cursor-pointer" : isHoverSupported ? "cursor-zoom-in" : "cursor-pointer";

  return (
    <div
      className={`relative inline-flex h-full w-full ${cursorClass} items-center justify-center ${className}`}
      onClick={handleClick}
      onMouseEnter={isHoverSupported ? handleMouseEnter : undefined}
      onMouseLeave={isHoverSupported ? handleMouseLeave : undefined}
      onMouseMove={isHoverSupported ? handleMouseMove : undefined}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick(event);
        }
      }}
      aria-label={alt}
    >
      <div className="relative h-full w-full">
        <img
          alt={alt}
          className="h-full w-full object-cover"
          src={src}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
        {isZoomImageLoading && isVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <Spinner className="!m-0 h-6 w-6 text-gray-400" />
          </div>
        )}
      </div>
      {zoomImagePreloader}
      {zoomElement}
    </div>
  );
};

export { ImageZoom };

